import { useEffect, useState, type RefObject } from 'react';

const DEFAULT_DELTA_THRESHOLD_PX = 4;
/** Ignore scroll deltas briefly after a toggle so layout/padding shifts cannot re-hide chrome. */
const LAYOUT_SETTLE_MS = 150;

export type UseScrollAwayChromeOptions = {
  /** Omit to listen to window scroll; pass an element to scope to that scroller. */
  scrollRoot?: HTMLElement | null;
  /** Minimum scroll delta (px) before toggling in boolean mode. Default 4. */
  deltaThresholdPx?: number;
  /**
   * When set, chrome hide tracks scroll distance 1:1 in `[0, maxOffsetPx]`
   * instead of snapping via a boolean transition.
   */
  maxOffsetPx?: number;
  /**
   * Host element for the CSS custom property (0–1 hide progress). Required for
   * distance mode so transforms/sticky offsets can follow scroll without React renders.
   */
  styleTargetRef?: RefObject<HTMLElement | null>;
  /** Custom property written as unitless 0–1. Default `--scroll-away-hide`. */
  cssVariableName?: string;
};

export type ScrollAwayChrome = {
  /** Hide distance in px when `maxOffsetPx` is set; otherwise `0` or `1`. */
  offsetPx: number;
  /** True when chrome should be treated as hidden (fully away, or boolean-mode hide). */
  away: boolean;
};

const DEFAULT_CSS_VAR = '--scroll-away-hide';

/**
 * Scroll-away chrome for toolbars / headers.
 *
 * - **Distance mode** (`maxOffsetPx`): hide offset follows scroll delta 1:1,
 *   written to a CSS variable on `styleTargetRef` for scroll-linked transforms.
 * - **Boolean mode** (default): scrolling down hides; any upward scroll restores.
 */
export function useScrollAwayChrome(
  enabled: boolean,
  options: UseScrollAwayChromeOptions = {},
): ScrollAwayChrome {
  const {
    scrollRoot,
    deltaThresholdPx = DEFAULT_DELTA_THRESHOLD_PX,
    maxOffsetPx,
    styleTargetRef,
    cssVariableName = DEFAULT_CSS_VAR,
  } = options;
  const distanceMode = maxOffsetPx != null && maxOffsetPx > 0;
  const [away, setAway] = useState(false);
  const [offsetPx, setOffsetPx] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setAway(false);
      setOffsetPx(0);
      const target = styleTargetRef?.current;
      if (target) {
        target.style.setProperty(cssVariableName, '0');
        delete target.dataset.scrollAway;
      }
      return;
    }

    const useWindow = scrollRoot === undefined;
    if (!useWindow && !scrollRoot) {
      setAway(false);
      setOffsetPx(0);
      return;
    }

    const getScrollTop = () => {
      if (!useWindow) return scrollRoot!.scrollTop;
      return window.scrollY || document.documentElement.scrollTop || 0;
    };

    let lastScrollTop = getScrollTop();
    let ignoreUntil = 0;
    let currentAway = false;
    let currentOffset = 0;
    let raf = 0;

    const writeCssVar = (offset: number) => {
      const target = styleTargetRef?.current;
      if (!target || maxOffsetPx == null) return;
      const progress = offset / maxOffsetPx;
      target.style.setProperty(cssVariableName, String(progress));
      // Let descendants disable hit-testing once mostly off-screen.
      target.dataset.scrollAway = progress >= 0.85 ? '1' : '0';
    };

    const onScroll = () => {
      const scrollTop = getScrollTop();
      const now = performance.now();

      if (!distanceMode && now < ignoreUntil) {
        lastScrollTop = scrollTop;
        return;
      }

      const delta = scrollTop - lastScrollTop;
      lastScrollTop = scrollTop;

      if (distanceMode) {
        if (scrollTop <= 0) {
          currentOffset = 0;
        } else {
          currentOffset = Math.min(maxOffsetPx!, Math.max(0, currentOffset + delta));
        }

        if (!raf) {
          raf = requestAnimationFrame(() => {
            raf = 0;
            // Visuals follow the CSS variable — avoid per-frame React renders.
            writeCssVar(currentOffset);
            const nextAway = currentOffset >= maxOffsetPx! - 0.5;
            if (nextAway !== currentAway) {
              currentAway = nextAway;
              setAway(nextAway);
              setOffsetPx(currentOffset);
            }
          });
        }
        return;
      }

      // Boolean mode — snap hide/show from scroll direction.
      let nextAway = currentAway;

      if (scrollTop <= 0) {
        nextAway = false;
      } else if (Math.abs(delta) >= deltaThresholdPx) {
        nextAway = delta > 0;
      }

      if (nextAway !== currentAway) {
        currentAway = nextAway;
        ignoreUntil = now + LAYOUT_SETTLE_MS;
        setAway(nextAway);
        setOffsetPx(nextAway ? 1 : 0);
      }
    };

    writeCssVar(0);

    if (useWindow) {
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', onScroll);
        if (raf) cancelAnimationFrame(raf);
      };
    }

    scrollRoot!.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scrollRoot!.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [
    enabled,
    scrollRoot,
    deltaThresholdPx,
    distanceMode,
    maxOffsetPx,
    styleTargetRef,
    cssVariableName,
  ]);

  return { offsetPx, away };
}
